/**
 * Tiny, dependency-free i18n for the embeddable widget.
 *
 * The widget is a singleton per page, so we configure a module-level
 * translator once at `init()` and let components call `t('key')` directly —
 * no prop/context threading, minimal churn, tree-shakeable.
 *
 * Locales mirror the dashboard (chat-ms-frontend): es (default), en, pt, fr, it.
 * Lookup falls back: requested locale → es → the key itself (so a missing
 * key is visible, never blank). `{var}` interpolation is supported.
 *
 * Bundle: plain object maps of short strings; no i18n library.
 */

export type Locale = 'es' | 'en' | 'pt' | 'fr' | 'it';

export const SUPPORTED_LOCALES: Locale[] = ['es', 'en', 'pt', 'fr', 'it'];
export const DEFAULT_LOCALE: Locale = 'es';

type Messages = Record<string, string>;

const es: Messages = {
  'composer.placeholder': 'Escribí tu mensaje...',
  'composer.send': 'Enviar',
  'header.title': 'Asistente de Compras',
  'header.subtitle': 'Te ayudo a encontrar lo que buscás',
  'composer.welcome': '¡Hola! 👋 Soy tu asistente de compras. ¿En qué puedo ayudarte hoy?',
  'messages.ariaLabel': 'Mensajes',
  'common.close': 'Cerrar',
  'product.add': 'Agregar',
  'cart.title': 'Tu carrito',
  'wallet.addCard': 'Agregar tarjeta',
  'wallet.addPaymentMethod': 'Agregar medio de pago',
  'wallet.secureCardNote': 'La tarjeta se carga dentro del checkout seguro.',
  'checkout.open': 'Abrir checkout',
  'checkout.unavailable': 'Link no disponible',
  'checkout.pay': 'Ir a pagar',
  'checkout.popupBlocked': "Si no se abrió la pestaña de pago, tocá el botón.",
  'payment.confirmed': 'Pago confirmado',
  'payment.pending': 'Pago pendiente',
  'payment.failed': 'Pago fallido',
  'payment.expired': 'Link expirado',
  'payment.refunded': 'Reembolsado',
  'payment.unknown': 'Estado desconocido',
  'login.region': 'Iniciar sesión',
  'login.email': 'Email',
  'login.sendCode': 'Enviar código',
  'login.sending': 'Enviando...',
  'login.sendCodeAria': 'Enviar código de acceso',
  'login.invalidEmail': 'Ingresá un email válido.',
  'login.codeLabel': 'Código de 6 dígitos',
  'login.verify': 'Verificar',
  'login.verifying': 'Verificando...',
  'login.verifyAria': 'Verificar código',
  'login.changeEmail': 'Cambiar email',
  'login.invalidCode': 'El código debe tener entre 4 y 8 dígitos.',
  'login.ssoGroup': 'Acceder con otro proveedor',
  'login.continueWith': 'Continuar con {provider}',
  'list.open': 'Ver opciones',
  'list.close': 'Cerrar',
};

const en: Messages = {
  'composer.placeholder': 'Type your message...',
  'composer.send': 'Send',
  'header.title': 'Shopping Assistant',
  'header.subtitle': 'I help you find what you need',
  'composer.welcome': 'Hi! 👋 I\'m your shopping assistant. How can I help you today?',
  'messages.ariaLabel': 'Messages',
  'common.close': 'Close',
  'product.add': 'Add',
  'cart.title': 'Your cart',
  'wallet.addCard': 'Add card',
  'wallet.addPaymentMethod': 'Add payment method',
  'wallet.secureCardNote': 'Your card is added inside the secure checkout.',
  'checkout.open': 'Open checkout',
  'checkout.unavailable': 'Link unavailable',
  'checkout.pay': 'Go to checkout',
  'checkout.popupBlocked': "If the payment tab did not open, tap the button.",
  'payment.confirmed': 'Payment confirmed',
  'payment.pending': 'Payment pending',
  'payment.failed': 'Payment failed',
  'payment.expired': 'Link expired',
  'payment.refunded': 'Refunded',
  'payment.unknown': 'Unknown status',
  'login.region': 'Sign in',
  'login.email': 'Email',
  'login.sendCode': 'Send code',
  'login.sending': 'Sending...',
  'login.sendCodeAria': 'Send access code',
  'login.invalidEmail': 'Enter a valid email.',
  'login.codeLabel': '6-digit code',
  'login.verify': 'Verify',
  'login.verifying': 'Verifying...',
  'login.verifyAria': 'Verify code',
  'login.changeEmail': 'Change email',
  'login.invalidCode': 'The code must be 4 to 8 digits.',
  'login.ssoGroup': 'Sign in with another provider',
  'login.continueWith': 'Continue with {provider}',
  'list.open': 'View options',
  'list.close': 'Close',
};

const pt: Messages = {
  'composer.placeholder': 'Escreva sua mensagem...',
  'composer.send': 'Enviar',
  'header.title': 'Assistente de Compras',
  'header.subtitle': 'Ajudo você a encontrar o que procura',
  'composer.welcome': 'Olá! 👋 Sou seu assistente de compras. Como posso ajudar hoje?',
  'messages.ariaLabel': 'Mensagens',
  'common.close': 'Fechar',
  'product.add': 'Adicionar',
  'cart.title': 'Seu carrinho',
  'wallet.addCard': 'Adicionar cartão',
  'wallet.addPaymentMethod': 'Adicionar forma de pagamento',
  'wallet.secureCardNote': 'O cartão é adicionado dentro do checkout seguro.',
  'checkout.open': 'Abrir checkout',
  'checkout.unavailable': 'Link indisponível',
  'checkout.pay': 'Ir para o pagamento',
  'checkout.popupBlocked': "Se a aba de pagamento não abriu, toque no botão.",
  'payment.confirmed': 'Pagamento confirmado',
  'payment.pending': 'Pagamento pendente',
  'payment.failed': 'Pagamento falhou',
  'payment.expired': 'Link expirado',
  'payment.refunded': 'Reembolsado',
  'payment.unknown': 'Status desconhecido',
  'login.region': 'Entrar',
  'login.email': 'Email',
  'login.sendCode': 'Enviar código',
  'login.sending': 'Enviando...',
  'login.sendCodeAria': 'Enviar código de acesso',
  'login.invalidEmail': 'Digite um email válido.',
  'login.codeLabel': 'Código de 6 dígitos',
  'login.verify': 'Verificar',
  'login.verifying': 'Verificando...',
  'login.verifyAria': 'Verificar código',
  'login.changeEmail': 'Alterar email',
  'login.invalidCode': 'O código deve ter de 4 a 8 dígitos.',
  'login.ssoGroup': 'Entrar com outro provedor',
  'login.continueWith': 'Continuar com {provider}',
  'list.open': 'Ver opções',
  'list.close': 'Fechar',
};

const fr: Messages = {
  'composer.placeholder': 'Écrivez votre message...',
  'composer.send': 'Envoyer',
  'header.title': 'Assistant d\'achat',
  'header.subtitle': 'Je vous aide à trouver ce que vous cherchez',
  'composer.welcome': 'Bonjour ! 👋 Je suis votre assistant d\'achat. Comment puis-je vous aider ?',
  'messages.ariaLabel': 'Messages',
  'product.add': 'Ajouter',
  'cart.title': 'Votre panier',
  'wallet.addCard': 'Ajouter une carte',
  'wallet.addPaymentMethod': 'Ajouter un moyen de paiement',
  'wallet.secureCardNote': 'La carte est ajoutée dans le paiement sécurisé.',
  'checkout.open': 'Ouvrir le paiement',
  'checkout.unavailable': 'Lien indisponible',
  'checkout.pay': 'Aller au paiement',
  'checkout.popupBlocked': "Si l'onglet de paiement ne s'est pas ouvert, touchez le bouton.",
  'payment.confirmed': 'Paiement confirmé',
  'payment.pending': 'Paiement en attente',
  'payment.failed': 'Paiement échoué',
  'payment.expired': 'Lien expiré',
  'payment.refunded': 'Remboursé',
  'payment.unknown': 'Statut inconnu',
  'login.region': 'Se connecter',
  'login.email': 'E-mail',
  'login.sendCode': 'Envoyer le code',
  'login.sending': 'Envoi...',
  'login.sendCodeAria': "Envoyer le code d'accès",
  'login.invalidEmail': 'Entrez un e-mail valide.',
  'login.codeLabel': 'Code à 6 chiffres',
  'login.verify': 'Vérifier',
  'login.verifying': 'Vérification...',
  'login.verifyAria': 'Vérifier le code',
  'login.changeEmail': "Changer d'e-mail",
  'login.invalidCode': 'Le code doit comporter de 4 à 8 chiffres.',
  'login.ssoGroup': 'Se connecter avec un autre fournisseur',
  'login.continueWith': 'Continuer avec {provider}',
  'list.open': 'Voir les options',
  'list.close': 'Fermer',
};

const it: Messages = {
  'composer.placeholder': 'Scrivi il tuo messaggio...',
  'composer.send': 'Invia',
  'header.title': 'Assistente per gli acquisti',
  'header.subtitle': 'Ti aiuto a trovare ciò che cerchi',
  'composer.welcome': 'Ciao! 👋 Sono il tuo assistente per gli acquisti. Come posso aiutarti?',
  'messages.ariaLabel': 'Messaggi',
  'common.close': 'Chiudi',
  'product.add': 'Aggiungi',
  'cart.title': 'Il tuo carrello',
  'wallet.addCard': 'Aggiungi carta',
  'wallet.addPaymentMethod': 'Aggiungi metodo di pagamento',
  'wallet.secureCardNote': 'La carta viene aggiunta nel checkout sicuro.',
  'checkout.open': 'Apri il checkout',
  'checkout.unavailable': 'Link non disponibile',
  'checkout.pay': 'Vai al pagamento',
  'checkout.popupBlocked': "Se la scheda di pagamento non si è aperta, tocca il pulsante.",
  'payment.confirmed': 'Pagamento confermato',
  'payment.pending': 'Pagamento in sospeso',
  'payment.failed': 'Pagamento fallito',
  'payment.expired': 'Link scaduto',
  'payment.refunded': 'Rimborsato',
  'payment.unknown': 'Stato sconosciuto',
  'login.region': 'Accedi',
  'login.email': 'Email',
  'login.sendCode': 'Invia codice',
  'login.sending': 'Invio...',
  'login.sendCodeAria': 'Invia codice di accesso',
  'login.invalidEmail': "Inserisci un'email valida.",
  'login.codeLabel': 'Codice di 6 cifre',
  'login.verify': 'Verifica',
  'login.verifying': 'Verifica...',
  'login.verifyAria': 'Verifica codice',
  'login.changeEmail': 'Cambia email',
  'login.invalidCode': 'Il codice deve avere da 4 a 8 cifre.',
  'login.ssoGroup': 'Accedi con un altro provider',
  'login.continueWith': 'Continua con {provider}',
  'list.open': 'Vedi opzioni',
  'list.close': 'Chiudi',
};

const MESSAGES: Record<Locale, Messages> = { es, en, pt, fr, it };

export function normalizeLocale(value: unknown): Locale {
  if (typeof value === 'string') {
    const short = value.toLowerCase().slice(0, 2) as Locale;
    if (SUPPORTED_LOCALES.indexOf(short) !== -1) return short;
  }
  return DEFAULT_LOCALE;
}

let activeLocale: Locale = DEFAULT_LOCALE;

/** Configure the active locale once at widget init. Unknown → es. */
export function configureI18n(locale?: unknown): Locale {
  activeLocale = normalizeLocale(locale);
  return activeLocale;
}

export function getLocale(): Locale {
  return activeLocale;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`,
  );
}

/**
 * Translate a key for the active locale.
 * Fallback chain: active locale → es → the key itself.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const fromActive = MESSAGES[activeLocale][key];
  const raw = fromActive !== undefined ? fromActive : MESSAGES[DEFAULT_LOCALE][key];
  return interpolate(raw !== undefined ? raw : key, vars);
}

/** Build a translator bound to an explicit locale (used by tests). */
export function createTranslator(locale: Locale) {
  return (key: string, vars?: Record<string, string | number>): string => {
    const raw = MESSAGES[locale][key] ?? MESSAGES[DEFAULT_LOCALE][key];
    return interpolate(raw !== undefined ? raw : key, vars);
  };
}
